"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { createArTableStore } from "@/components/ui/ar-table";
import { AvatarNameBadge } from "@/components/ui/avatar-name-badge";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { TableRowContextMenu } from "@/components/ui/table-row-context-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyOverlay,
  TableFillerRow,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  useTableSkeletonCount,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api/error";
import type { RowOut, SortIn } from "@/lib/api/model";
import { cn } from "@/lib/utils";

import { useConversationColumns } from "../../hooks/use-conversation-columns";
import {
  useConversationsQuery,
  type QueryScope,
} from "../../hooks/use-conversations-query";
import { COPY } from "../../lib/copy";
import { attentionHint, attentionLabel, meetingTypeLabel } from "../../lib/labels";
import { CONVERSATIONS_ROUTES } from "../../lib/routes";
import { useConversationsStore } from "../../store/conversations-store";
import { EntityBadge } from "../shared/entity-badge";
import { StateChip } from "../shared/state-chip";
import { JoinMeetingLink } from "../shared/join-meeting-link";
import { WhySkipped, showsWhy } from "../shared/why-skipped";
import { ConversationsSkeleton } from "./pending";
import { RowActionsContextMenuContent, RowActionsMenu } from "./row-actions-menu";
import { useListFilterParam } from "./use-list-filters";

const CONVERSATION_COLUMNS = [
  { id: "title", label: COPY.columns.title, visible: true, position: 0, width: 320 },
  { id: "meeting_type", label: COPY.columns.meeting_type, visible: true, position: 1, width: 140 },
  { id: "state", label: COPY.columns.state, visible: true, position: 2, width: 140 },
  { id: "entity", label: COPY.columns.entity, visible: true, position: 3, width: 240 },
  { id: "participants", label: COPY.columns.participants, visible: true, position: 4, width: 200 },
  { id: "scheduled_start", label: COPY.columns.scheduled_start, visible: true, position: 5, width: 210 },
  { id: "attention", label: COPY.columns.attention, visible: true, position: 6, width: 200 },
];

const SORTABLE = ["title", "state", "scheduled_start"];
const PER_PAGE = 50;

export const useConversationsTableStore = createArTableStore({
  persistKey: "conversations-table",
  defaultColumns: CONVERSATION_COLUMNS,
});

export const useConversationsAttentionTableStore = createArTableStore({
  persistKey: "conversations-table-attention",
  defaultColumns: CONVERSATION_COLUMNS,
});

export const useConversationsJobTableStore = createArTableStore({
  persistKey: "conversations-table-job",
  defaultColumns: CONVERSATION_COLUMNS,
});

export const useConversationsCompactTableStore = createArTableStore({
  persistKey: "conversations-table-compact",
  defaultColumns: CONVERSATION_COLUMNS,
});

export type ConversationsTableSurface = "list" | "attention" | "job" | "compact";

/** Every surface's store comes from the same factory, so one hook type fits all. */
type TableStoreHook = typeof useConversationsTableStore;

const TABLE_STORES: Record<ConversationsTableSurface, TableStoreHook> = {
  list: useConversationsTableStore,
  attention: useConversationsAttentionTableStore,
  job: useConversationsJobTableStore,
  compact: useConversationsCompactTableStore,
};

function ListError({
  error,
  onRetry,
  onReload,
}: {
  error: unknown;
  onRetry: () => void;
  onReload: () => void;
}) {
  const stale = error instanceof ApiError && error.code === "COLUMNS_STALE";
  return (
    <div className="flex flex-1 items-center justify-center bg-surface-primary px-6 py-16">
      <div
        role="alert"
        className="flex max-w-sm flex-col items-center gap-3 text-center"
      >
        <div
          aria-hidden="true"
          className="flex size-14 items-center justify-center rounded-xl border border-border bg-surface-inset"
        >
          <Icon
            name="IconExclamationCircle"
            size={24}
            className="text-heading opacity-30"
          />
        </div>
        <p className="text-base font-medium text-heading">
          {stale ? COPY.errors.columnsStale : COPY.errors.loadFailed}
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={stale ? onReload : onRetry}
        >
          {stale ? COPY.actions.reload : COPY.actions.retry}
        </Button>
      </div>
    </div>
  );
}

function SortHead({
  column,
  label,
  className,
  useStore,
}: {
  column: string;
  label: string;
  className?: string;
  useStore: TableStoreHook;
}) {
  const sorts = useStore((s) => s.sorts);
  const setSort = useStore((s) => s.setSort);
  const entry = sorts.find((s) => s.column === column);
  if (!SORTABLE.includes(column)) {
    return <TableHead className={className}>{label}</TableHead>;
  }
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => setSort(column)}
        className="group/sort flex max-w-full items-center gap-1 text-left text-sm font-medium text-muted-foreground transition-colors hover:text-heading"
      >
        <span className="truncate">{label}</span>
        <Icon
          name={entry?.order === "asc" ? "IconArrowUp" : "IconArrowDown"}
          size={12}
          aria-hidden="true"
          className={cn(
            "shrink-0 text-heading transition-opacity",
            entry ? "opacity-60" : "opacity-0 group-hover/sort:opacity-30",
          )}
        />
      </button>
    </TableHead>
  );
}

function ConversationsTHead({
  sticky,
  compact,
  useStore,
}: {
  sticky?: boolean;
  compact?: boolean;
  useStore: TableStoreHook;
}) {
  if (compact) {
    return (
      <TableHeader sticky={sticky}>
        <TableRow>
          <SortHead
            column="title"
            label={COPY.columns.title}
            className="w-[45%] min-w-0"
            useStore={useStore}
          />
          <SortHead
            column="state"
            label={COPY.columns.state}
            className="w-[25%] min-w-0"
            useStore={useStore}
          />
          <SortHead
            column="scheduled_start"
            label={COPY.columns.scheduled_start}
            className="w-[30%]"
            useStore={useStore}
          />
          <TableHead className="w-10" aria-label={COPY.columns.actions} />
        </TableRow>
      </TableHeader>
    );
  }
  return (
    <TableHeader sticky={sticky}>
      <TableRow>
        <SortHead
          column="title"
          label={COPY.columns.title}
          className="w-[25%] min-w-0"
          useStore={useStore}
        />
        <SortHead
          column="state"
          label={COPY.columns.state}
          className="w-[12%] min-w-0"
          useStore={useStore}
        />
        <TableHead className="w-[18%] min-w-0">{COPY.columns.entity}</TableHead>
        <TableHead className="w-[16%] min-w-0">
          {COPY.columns.participants}
        </TableHead>
        <SortHead
          column="scheduled_start"
          label={COPY.columns.scheduled_start}
          className="w-[14%]"
          useStore={useStore}
        />
        <TableHead className="w-[15%] min-w-0">
          {COPY.columns.attention}
        </TableHead>
        <TableHead className="w-10" aria-label={COPY.columns.actions} />
      </TableRow>
    </TableHeader>
  );
}

function shortDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function TitleCell({ row }: { row: RowOut }) {
  return (
    <TableCell className="min-w-0">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-1">
          <div className="shrink-0 opacity-50">
            <Icon name="IconVideo" size={14} className="text-heading" />
          </div>
          <Link
            href={CONVERSATIONS_ROUTES.conversation(row.id)}
            onClick={(e) => e.stopPropagation()}
            className="min-w-0 truncate text-sm text-contrast/80 hover:text-heading"
          >
            {row.title || COPY.untitled}
          </Link>
          <JoinMeetingLink url={row.meeting_url} state={row.state} className="ml-auto" />
        </div>
        <span className="min-w-0 truncate pl-[18px] text-[13px] text-contrast/50">
          {meetingTypeLabel(row.meeting_type)}
        </span>
      </div>
    </TableCell>
  );
}

function AttentionCell({ row }: { row: RowOut }) {
  const groups = [...new Set(row.attention)];
  if (groups.length === 0) {
    return (
      <TableCell className="min-w-0">
        <span className="text-sm text-contrast/30">—</span>
      </TableCell>
    );
  }
  const shown = groups.slice(0, 2);
  const rest = groups.slice(2);
  return (
    <TableCell className="min-w-0">
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {shown.map((group) => (
          <Tooltip key={group}>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex max-w-full">
                <Badge variant="warning" size="xs" className="max-w-full rounded-md leading-4">
                  <span className="truncate">{attentionLabel(group)}</span>
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">{attentionHint(group)}</TooltipContent>
          </Tooltip>
        ))}
        {rest.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="text-xs text-contrast/50">
                +{rest.length}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">
              {rest.map((group) => attentionHint(group)).join(" ")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TableCell>
  );
}

function ParticipantsCell({ row }: { row: RowOut }) {
  const names = row.participants.map((p) => p.display_name ?? p.role);
  if (names.length === 0) {
    return (
      <TableCell className="min-w-0">
        <span className="text-sm text-contrast/40">—</span>
      </TableCell>
    );
  }
  return (
    <TableCell className="min-w-0">
      <div className="flex min-w-0 items-center gap-1">
        <AvatarNameBadge
          name={names[0]}
          avatarUrl={null}
          className="min-w-0 max-w-full shrink overflow-hidden [&>span]:truncate"
        />
        {names.length > 1 && (
          <span className="shrink-0 text-xs text-contrast/50">
            +{names.length - 1}
          </span>
        )}
      </div>
    </TableCell>
  );
}

export function ConversationsTable({
  scope,
  attention,
  emptyText,
  surface = "list",
  emptyAction,
}: {
  scope: QueryScope;
  attention?: string;
  emptyText?: string;
  surface?: ConversationsTableSurface;
  /** Rendered in the empty overlay when no filters are active (03: the Upload button). */
  emptyAction?: ReactNode;
}) {
  const router = useRouter();
  const isCompact = surface === "compact";
  const useStore = TABLE_STORES[surface];
  const columns = useConversationColumns();
  const { filters, setFilters } = useListFilterParam();
  const page = useStore((s) => s.page);
  const setPage = useStore((s) => s.setPage);
  const sorts = useStore((s) => s.sorts);
  const loadingRef = useRef<HTMLDivElement>(null);
  const emptyRef = useRef<HTMLDivElement>(null);
  const sort = useMemo<SortIn | undefined>(() => {
    const first = sorts[0];
    if (!first) return undefined;
    return { column: first.column, dir: first.order };
  }, [sorts]);
  const queryIdentity = JSON.stringify([scope, attention, filters]);
  const prevIdentity = useRef(queryIdentity);
  useEffect(() => {
    setPage(1);
  }, [setPage]);
  useEffect(() => {
    if (prevIdentity.current === queryIdentity) return;
    prevIdentity.current = queryIdentity;
    setPage(1);
  }, [queryIdentity, setPage]);
  const q = useConversationsQuery(scope, {
    filters,
    attention,
    sort,
    page,
    per_page: PER_PAGE,
  });
  const live = useConversationsStore((s) => s.byId);
  // Re-sorting or paging swaps the query key, so react-query reports
  // `isLoading` again with no data. Swapping the whole table for skeleton rows
  // then would tear the header out from under the click that caused it; show
  // the skeleton for the first load only and let later fetches keep the
  // header and footer mounted (what `ArTable` did with its own loading flag).
  const [loadedOnce, setLoadedOnce] = useState(false);
  if (q.data && !loadedOnce) setLoadedOnce(true);
  const showSkeleton = q.isLoading && !loadedOnce;
  const skeletonCount = useTableSkeletonCount(loadingRef, {
    active: showSkeleton,
  });
  const emptySkeletonCount = useTableSkeletonCount(emptyRef);

  const rows: RowOut[] = useMemo(
    () => (q.data?.rows ?? []).map((r) => ({ ...r, ...(live[r.id] ?? {}) })),
    [q.data, live],
  );
  const total = q.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const showEmpty = Boolean(q.data && q.data.total === 0) && !q.error;
  const emptyMessage = emptyText ?? COPY.empty.all;
  const columnCount = isCompact ? 4 : 7;

  if (q.error) {
    return (
      <ListError
        error={q.error}
        onRetry={() => {
          void columns.refetch();
          void q.refetch();
        }}
        onReload={() => window.location.reload()}
      />
    );
  }

  if (showSkeleton) {
    return (
      <div
        ref={loadingRef}
        className="flex flex-1 flex-col overflow-hidden bg-surface-primary"
      >
        <Table variant="list" layout="fixed">
          <ConversationsTHead compact={isCompact} useStore={useStore} />
          <ConversationsSkeleton count={skeletonCount} compact={isCompact} />
        </Table>
      </div>
    );
  }

  if (showEmpty) {
    return (
      <TableEmptyOverlay
        containerRef={emptyRef}
        icon="IconVideo"
        title={emptyMessage}
        action={
          filters.length > 0 ? (
            <TableEmptyOverlay.ClearFiltersButton
              onClick={() => setFilters([])}
            />
          ) : (
            emptyAction ?? null
          )
        }
        backdrop={
          <Table variant="list" layout="fixed">
            <ConversationsTHead compact={isCompact} useStore={useStore} />
            <ConversationsSkeleton
              count={emptySkeletonCount}
              compact={isCompact}
            />
          </Table>
        }
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden bg-surface-primary">
        <Table variant="list" layout="fixed">
          <ConversationsTHead sticky compact={isCompact} useStore={useStore} />
          <TableBody>
            {rows.map((r) => (
              <TableRowContextMenu key={r.id} content={<RowActionsContextMenuContent row={r} />}>
              <TableRow
                rowLabel={r.title || COPY.untitled}
                onClick={() =>
                  router.push(CONVERSATIONS_ROUTES.conversation(r.id))
                }
              >
                <TitleCell row={r} />
                <TableCell className="min-w-0">
                  <div className="flex min-w-0 flex-col gap-1">
                    <StateChip state={r.state} stateReason={r.state_reason} />
                    {showsWhy(r.state) && r.state_reason && (
                      <WhySkipped reason={r.state_reason} className="pl-0.5" />
                    )}
                  </div>
                </TableCell>
                {!isCompact && (
                  <TableCell className="min-w-0">
                    <EntityBadge
                      binding={r.binding}
                      job={r.job}
                      jobCandidate={r.job_candidate}
                      matchNotNeeded={r.match_not_needed}
                    />
                  </TableCell>
                )}
                {!isCompact && <ParticipantsCell row={r} />}
                <TableCell>
                  <span className="whitespace-nowrap text-sm text-contrast/80">
                    {shortDate(r.scheduled_start)}
                  </span>
                </TableCell>
                {!isCompact && <AttentionCell row={r} />}
                <TableCell>
                  <RowActionsMenu row={r} />
                </TableCell>
              </TableRow>
              </TableRowContextMenu>
            ))}
            <TableFillerRow columns={columnCount} />
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 ? (
        <TablePagination
          page={page}
          total={total}
          perPage={PER_PAGE}
          onPageChange={setPage}
          label={(t) => `${t} meeting${t !== 1 ? "s" : ""}`}
          disabled={q.isFetching}
        />
      ) : isCompact ? null : (
        <div className="flex h-12 shrink-0 items-center border-t border-border bg-surface-elevated px-4">
          <p className="whitespace-nowrap text-sm font-medium text-contrast/70">
            {total} meeting{total !== 1 ? "s" : ""}
          </p>
        </div>
      )}
    </div>
  );
}
